<template>
	<div v-editable="blok">
		<NuxtLink
			:to="to"
			class="cms-element-image flex relative w-full h-full cursor-pointer group category-hero"
			:class="[
				blok.aspect_ratio === 'aspect-square'
					? 'pb-[100%] category-hero--square'
					: 'pb-[68%]',
				maxHeightStyle && 'category-hero--capped'
			]"
			:style="maxHeightStyle"
		>
			<div
				v-if="!blok.text_below"
				class="absolute w-full h-full z-10 flex items-center justify-center"
				:class="blok.overlay || 'bg-black/30'"
			>
				<div
					v-if="blok.button_text"
					class="category-hero__content flex flex-col items-center gap-4 text-center"
				>
					<span class="category-hero__title text-xl text-white">
						{{ blok.name }}
					</span>
					<span
						class="category-hero__button bg-white px-6 py-3 text-sm font-medium text-primary"
					>
						{{ blok.button_text }}
					</span>
				</div>
				<button
					v-else
					class="button-banner-button !bg-transparent group-hover:!bg-blue-light"
				>
					{{ blok.name }}
				</button>
			</div>
			<div
				v-else-if="blok.overlay"
				class="absolute w-full h-full z-10"
				:class="blok.overlay"
			/>
			<NuxtImg
				v-if="image"
				:src="image.src"
				:provider="image.provider"
				:alt="blok.button_text ? '' : blok.name"
				:loading="lazyLoadImages ? 'lazy' : 'eager'"
				:preload="lazyLoadImages ? false : { fetchPriority: 'high' }"
				:fetchpriority="lazyLoadImages ? 'auto' : 'high'"
				format="webp"
				quality="80"
				class="h-full w-full absolute inset-0 object-cover object-center"
				:class="blok.aspect_ratio || ''"
				:sizes="sizes"
				:densities="CATEGORY_TILE_DENSITIES"
			/>
		</NuxtLink>
		<NuxtLink
			v-if="blok.text_below"
			:to="to"
			class="block pt-3 text-center font-medium category-hero-label"
		>
			{{ blok.name }}
		</NuxtLink>
	</div>
</template>

<script setup lang="ts">
import type { CategoryHeroBlok } from "~/types/storyblok/storyblok";
import {
	CATEGORY_TILE_DENSITIES,
	CATEGORY_TILE_SIZES
} from "~/layers/base/utils/categoryTileSizes";
import { toCmsImageSource } from "~/layers/base/utils/cmsImageSource";
import { categoryHeroId } from "~/utils/storyblok/categoryHeroId";
import { categoryHeroMaxHeight } from "~/utils/storyblok/categoryHeroMaxHeight";

interface Props {
	blok: CategoryHeroBlok;
	lazyLoadImages?: boolean;
	/** NuxtImg `sizes` for the grid the tile sits in (see `categoryTileSizes`). */
	sizes?: string;
}
const { blok, sizes = CATEGORY_TILE_SIZES } = defineProps<Props>();

const localePath = useLocalePath();
const { resolveLink } = useStoryblokLink();
// The one place a category tile's link is resolved: `blok.link` (story/url) or,
// when it is empty, the tile's Norce category (new `category_id`, else the old
// `id`). Resolved once at setup rather than in a computed, because the category
// fallback reads the i18n locale and the category store, which must not run
// lazily.
const to = resolveLink(blok.link, categoryHeroId(blok)) || localePath("/");

// Editor-set height caps (data, so CSS variables); a template's CSS applies
// them through `category-hero--capped`.
const maxHeightStyle = computed(() =>
	categoryHeroMaxHeight(blok.max_height_desktop, blok.max_height_mobile)
);

// Storyblok tiles go through the image service; any other host is left as is.
// Without a file there is no image, so the tile keeps its overlay and name.
const image = computed(() => toCmsImageSource(blok?.image?.filename));
</script>
